import { api } from '@/shared/lib/api';

export interface WhatsAppInstance {
  instanceId: string;
  instanceName: string;
  status: 'connected' | 'disconnected' | 'connecting';
  qrCode?: string;
  phone?: string;
  lastStatusUpdate?: string;
}

/** Como a IA processa as mensagens de um grupo de origem */
export interface GroupAIProfile {
  model: string;
  modelLabel: string;
  content: 'texto' | 'texto_imagem' | 'imagem';
  contentLabel: string;
  contentDetail: string;
  promptFile: string;
  customPrompt: boolean;
}

export interface WhatsAppGroupBasic {
  groupJid: string;
  groupName: string;
  groupPictureUrl?: string;
  participantCount: number;
  description?: string;
  createdAt: string;
  isPromotionSource?: boolean;
  isPromotionDestination?: boolean;
  /** Só em grupos de origem */
  aiProfile?: GroupAIProfile | null;
}

export interface InstanceStatusResponse {
  instance: WhatsAppInstance;
}

export interface GroupsListResponse {
  groups: WhatsAppGroupBasic[];
}

export interface PromotionMessage {
  id: string;
  sourceGroupJid: string;
  messageWid: string;
  senderNumber: string;
  senderName: string;
  messageText: string;
  timestamp: string;
  processingStatus: 'PENDING' | 'PROCESSING' | 'PROCESSED' | 'SENT' | 'FAILED' | 'IGNORED';
  aiPrompt?: string;
  aiResponse?: string;
  processedAt?: string;
  processingError?: string;
  destinationGroupJid?: string;
  destinationMessageWid?: string;
  sentAt?: string;
  sendingError?: string;
  hasMedia: boolean;
  mediaType?: string;
  createdAt: string;
  updatedAt: string;
}

export interface MessagesListResponse {
  data: PromotionMessage[];
}

export interface DashboardMetrics {
  today: {
    received: number;
    approved: number;
    discarded: number;
    needsReview: number;
    errors: number;
    approvalRate: number;
  };
  total: {
    received: number;
    approved: number;
    discarded: number;
    needsReview: number;
    errors: number;
    approvalRate: number;
  };
}

export interface DashboardMetricsResponse {
  success: boolean;
  data: DashboardMetrics;
}

export const promotionGroupsAPI = {
  /**
   * Busca o status da instância WhatsApp
   */
  getInstanceStatus: async (instanceId: string): Promise<InstanceStatusResponse> => {
    const response = await api.get(`/whatsapp/instances/${instanceId}/status`);
    return response.data;
  },

  /**
   * Busca todos os grupos da instância
   */
  getInstanceGroups: async (instanceId: string): Promise<GroupsListResponse> => {
    const response = await api.get(`/whatsapp/instances/${instanceId}/groups`);
    return response.data;
  },

  /**
   * Sincroniza grupos da instância com ZAPI
   */
  syncGroups: async (instanceId: string): Promise<void> => {
    await api.post(`/whatsapp/instances/${instanceId}/sync-groups`);
  },

  /**
   * Busca mensagens de promoção de um grupo específico
   */
  getGroupMessages: async (groupJid: string, limit: number = 100): Promise<MessagesListResponse> => {
    const response = await api.get(`/whatsapp/promotion-groups/${groupJid}/messages`, {
      params: { limit }
    });
    return response.data;
  },

  /**
   * Busca métricas do dashboard
   */
  getDashboardMetrics: async (): Promise<DashboardMetricsResponse> => {
    const response = await api.get('/whatsapp/promotion-groups/dashboard-metrics');
    return response.data;
  },
};
